import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageSquare, Users } from 'lucide-react';
import { assistantService } from '../services';
import type { Assistant } from '../types/assistant';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import AssistantSelector from '../components/chat/AssistantSelector';
import ChatInterface from '../components/chat/ChatInterface';
import LoadingSpinner from '../components/ui/LoadingSpinner';

function Chat() {
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(null);

  // Fetch assistants
  const { data: assistants, isLoading } = useQuery({
    queryKey: ['assistants'],
    queryFn: () => assistantService.getAssistants(),
  });

  if (isLoading) {
    return (
      <div className="py-6">
        <div className="px-4 sm:px-6 lg:px-8">
          <PageHeader
            title="Central de Conversas"
            description="Converse com seus assistentes de inteligência artificial."
          />
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    );
  }

  if (!assistants || assistants.length === 0) {
    return (
      <div className="py-6">
        <div className="px-4 sm:px-6 lg:px-8">
          <PageHeader
            title="Central de Conversas"
            description="Converse com seus assistentes de inteligência artificial."
          />
          <EmptyState
            icon={<MessageSquare className="h-12 w-12" />}
            title="Nenhum assistente disponível"
            description="Você precisa criar pelo menos um assistente para iniciar uma conversa."
            action={{
              label: 'Criar Assistente',
              onClick: () => (window.location.href = '/assistants'),
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Central de Conversas"
          description="Converse com seus assistentes de inteligência artificial."
        />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Assistant Selector */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 h-[600px] flex flex-col">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2" />
                  Assistentes
                </h2>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <AssistantSelector
                  assistants={assistants}
                  selectedAssistant={selectedAssistant}
                  onSelectAssistant={setSelectedAssistant}
                />
              </div>
            </div>
          </div>

          {/* Chat Interface */}
          <div className="lg:col-span-3">
            {selectedAssistant ? (
              <ChatInterface assistant={selectedAssistant} />
            ) : (
              <div className="h-[600px] flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                <div className="text-center">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                    Selecione um assistente
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Escolha um assistente na lista ao lado para iniciar uma conversa.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Chat;
